import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Flame, Zap, Droplets, TrendingUp, Scale, Target,
  Apple, ChevronRight, Clock, Edit3, Check, X,
  Activity, Award, Utensils, BarChart2
} from 'lucide-react';
import { useNutritionContext } from '../hooks/useNutritionContext';
import { profileAPI, goalsAPI } from '../services/apiService';
import { getCentralDate } from '../utils/dateUtils';

// Mifflin-St Jeor inline recalc (mirrors MacroCalculator.jsx exactly)
const recalcGoals = (profile) => {
  const weightKg = profile.weight_unit === 'kg'
    ? parseFloat(profile.weight)
    : parseFloat(profile.weight) / 2.2046;
  const heightCm = profile.height_unit === 'cm'
    ? parseFloat(profile.height)
    : parseFloat(profile.height) * 2.54;
  const age = parseInt(profile.age);
  const bmr = profile.gender === 'male'
    ? (10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5
    : (10 * weightKg) + (6.25 * heightCm) - (5 * age) - 161;
  const tdee = bmr * (parseFloat(profile.activity_level) || 1.375);
  const adj = profile.goal === 'lose' ? -500 : profile.goal === 'gain' ? 300 : 0;
  const calories = Math.max(1200, Math.round(tdee + adj));
  const protein = Math.round(weightKg * 2.2);
  const fat = Math.round(calories * 0.25 / 9);
  const carbs = Math.max(0, Math.round((calories - protein * 4 - fat * 9) / 4));
  return { calories, protein, carbs, fat, fiber: 30 };
};

// SVG Calorie Ring
const CalorieRing = ({ consumed, goal }) => {
  const pct = goal > 0 ? Math.min(consumed / goal, 1) : 0;
  const r = 54;
  const circ = 2 * Math.PI * r;
  const over = consumed > goal;
  const color = over ? '#ef4444' : pct > 0.9 ? '#f59e0b' : '#6366f1';
  return (
    <svg viewBox="0 0 120 120" className="w-36 h-36">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#f1f5f9" strokeWidth="10" />
      <circle
        cx="60" cy="60" r={r} fill="none"
        stroke={color} strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - pct)}
        transform="rotate(-90 60 60)"
        style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s' }}
      />
      <text x="60" y="54" textAnchor="middle" className="fill-gray-900 font-bold" fontSize="16" fontWeight="700">{Math.round(consumed)}</text>
      <text x="60" y="70" textAnchor="middle" fill="#94a3b8" fontSize="9">of {goal}</text>
      <text x="60" y="82" textAnchor="middle" fill="#94a3b8" fontSize="9">kcal</text>
    </svg>
  );
};

// Slim macro progress bar
const MacroBar = ({ label, consumed, goal, unit, color, bgColor }) => {
  const pct = goal > 0 ? Math.min((consumed / goal) * 100, 100) : 0;
  const over = consumed > goal;
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-xs font-semibold text-gray-600">{label}</span>
        <span className="text-xs text-gray-500">
          <span className={`font-bold ${over ? 'text-red-500' : 'text-gray-800'}`}>{Math.round(consumed)}</span>
          <span className="text-gray-400"> / {goal}{unit}</span>
        </span>
      </div>
      <div className={`h-2 rounded-full ${bgColor}`}>
        <div
          className={`h-2 rounded-full transition-all duration-500 ${over ? 'bg-red-400' : color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default function DashboardNew() {
  const { state, dispatch } = useNutritionContext();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [greeting, setGreeting] = useState('');
  const [editingWeight, setEditingWeight] = useState(false);
  const [weightInput, setWeightInput] = useState('');
  const [savingWeight, setSavingWeight] = useState(false);
  const weightInputRef = useRef(null);

  const today = getCentralDate();
  const todayEntries = (state.foodEntries || []).filter(e => e && e.date === today);
  const todayTotals = todayEntries.reduce(
    (acc, e) => ({
      calories: acc.calories + (parseFloat(e.calories) || 0),
      protein: acc.protein + (parseFloat(e.protein) || 0),
      carbs: acc.carbs + (parseFloat(e.carbs) || 0),
      fat: acc.fat + (parseFloat(e.fat) || 0),
      fiber: acc.fiber + (parseFloat(e.fiber) || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
  );

  const goals = state.dailyGoals;
  const remaining = {
    calories: Math.max(0, goals.calories - todayTotals.calories),
    protein: Math.max(0, goals.protein - todayTotals.protein),
    carbs: Math.max(0, goals.carbs - todayTotals.carbs),
    fat: Math.max(0, goals.fat - todayTotals.fat),
  };

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening');
    profileAPI.get().then(p => {
      if (p) { setProfile(p); setWeightInput(String(p.weight || '')); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (editingWeight && weightInputRef.current) weightInputRef.current.focus();
  }, [editingWeight]);

  const handleSaveWeight = async () => {
    const newWeight = parseFloat(weightInput);
    if (!newWeight || newWeight <= 0 || !profile) return;
    setSavingWeight(true);
    try {
      const updatedProfile = { ...profile, weight: newWeight };
      await profileAPI.update(updatedProfile);
      setProfile(updatedProfile);

      // Recalculate goals with new weight and sync everywhere
      if (profile.age && profile.height && profile.gender) {
        const newGoals = recalcGoals(updatedProfile);
        await goalsAPI.update(newGoals);
        dispatch({ type: 'UPDATE_DAILY_GOALS', payload: newGoals });
      }
      setEditingWeight(false);
    } catch (e) {
      console.error('Failed to save weight:', e);
    } finally {
      setSavingWeight(false);
    }
  };

  const userName = profile?.name?.split(' ')[0] || localStorage.getItem('userName')?.split(' ')[0] || '';
  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  // Group today's meals by type for recent meals section
  const recentMeals = [...todayEntries].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 5);

  // Calorie burn estimation (BMR-based, rough)
  const caloriesBurned = profile?.weight
    ? Math.round(
        (profile.gender === 'female'
          ? 447.6 + (9.25 * (profile.weight / 2.2046)) + (3.098 * (profile.height_unit === 'cm' ? profile.height : profile.height * 2.54)) - (4.33 * (profile.age || 30))
          : 88.36 + (13.4 * (profile.weight / 2.2046)) + (4.8 * (profile.height_unit === 'cm' ? profile.height : profile.height * 2.54)) - (5.7 * (profile.age || 30))
        ) / 24 * new Date().getHours()
      )
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

      {/* Top greeting bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {greeting}{userName ? `, ${userName}` : ''} 👋
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{dateStr}</p>
        </div>
        <Link
          to="/tracker"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          <Utensils className="w-4 h-4" /> Log a Meal
        </Link>
      </div>

      {/* Hero row: calorie ring + macros */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* Ring */}
          <div className="flex flex-col items-center flex-shrink-0">
            <CalorieRing consumed={todayTotals.calories} goal={goals.calories} />
            <div className="mt-3 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Remaining</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{Math.round(remaining.calories)}</p>
              <p className="text-xs text-gray-400">kcal</p>
            </div>
          </div>

          {/* Macros */}
          <div className="flex-1 w-full space-y-4">
            <MacroBar label="Protein" consumed={todayTotals.protein} goal={goals.protein} unit="g" color="bg-orange-500" bgColor="bg-orange-100" />
            <MacroBar label="Carbs"   consumed={todayTotals.carbs}   goal={goals.carbs}   unit="g" color="bg-blue-500"   bgColor="bg-blue-100"   />
            <MacroBar label="Fat"     consumed={todayTotals.fat}     goal={goals.fat}     unit="g" color="bg-yellow-400" bgColor="bg-yellow-100" />
            <MacroBar label="Fiber"   consumed={todayTotals.fiber}   goal={goals.fiber || 30}   unit="g" color="bg-green-500"  bgColor="bg-green-100"  />
          </div>

          {/* Right stat strip */}
          <div className="flex flex-row md:flex-col gap-4 flex-shrink-0">
            {/* Weight — editable */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[120px]">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Weight</p>
                {!editingWeight && (
                  <button onClick={() => setEditingWeight(true)} className="text-gray-400 hover:text-indigo-500 transition-colors">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {editingWeight ? (
                <div className="flex items-center gap-1 mt-1">
                  <input
                    ref={weightInputRef}
                    type="number"
                    value={weightInput}
                    onChange={e => setWeightInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveWeight(); if (e.key === 'Escape') setEditingWeight(false); }}
                    className="w-16 text-sm font-bold border border-indigo-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                    step="0.1"
                  />
                  <button onClick={handleSaveWeight} disabled={savingWeight} className="text-green-500 hover:text-green-600">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setEditingWeight(false); setWeightInput(String(profile?.weight || '')); }} className="text-red-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {profile?.weight ? `${profile.weight}` : '—'}
                  <span className="text-xs font-normal text-gray-400 ml-1">{profile?.weight_unit || 'lbs'}</span>
                </p>
              )}
            </div>

            {/* Meals logged */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[120px]">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">Meals Today</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{todayEntries.length}</p>
              <p className="text-xs text-gray-400">entries</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Goals */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Your Daily Goals</h2>
          <Link to="/macro-calculator" className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold hover:underline flex items-center gap-0.5">
            Edit goals <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Calories',  val: goals.calories, unit: 'kcal', bg: 'bg-indigo-50 dark:bg-indigo-900/20', num: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-100 dark:border-indigo-800' },
            { label: 'Protein',   val: goals.protein,  unit: 'g',    bg: 'bg-orange-50 dark:bg-orange-900/20', num: 'text-orange-700 dark:text-orange-300', border: 'border-orange-100 dark:border-orange-800' },
            { label: 'Carbs',     val: goals.carbs,    unit: 'g',    bg: 'bg-blue-50 dark:bg-blue-900/20',     num: 'text-blue-700 dark:text-blue-300',     border: 'border-blue-100 dark:border-blue-800' },
            { label: 'Fat',       val: goals.fat,      unit: 'g',    bg: 'bg-yellow-50 dark:bg-yellow-900/20', num: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-100 dark:border-yellow-800' },
          ].map((g, i) => (
            <div key={i} className={`rounded-xl border p-4 ${g.bg} ${g.border}`}>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">{g.label}</p>
              <p className={`text-2xl font-bold ${g.num}`}>
                {g.val}
                <span className="text-xs font-normal text-gray-400 ml-1">{g.unit}</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">daily target</p>
            </div>
          ))}
        </div>
      </div>

      {/* Middle row: recent meals + quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Recent meals — 2 cols */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Today's Meals</h2>
            <Link to="/tracker" className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-0.5 hover:underline">
              View all <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {recentMeals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <Apple className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">Nothing logged yet today</p>
              <Link to="/tracker" className="mt-3 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors">
                Log Your First Meal
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-50 dark:divide-gray-700">
              {recentMeals.map((entry, i) => (
                <div key={entry.id || i} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                      <Utensils className="w-4 h-4 text-indigo-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-tight">{entry.name}</p>
                      <p className="text-xs text-gray-400">{entry.mealType || 'Meal'} · {entry.servings || 1} serving</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{Math.round(entry.calories)} kcal</p>
                    <p className="text-xs text-gray-400">{Math.round(entry.protein)}P · {Math.round(entry.carbs)}C · {Math.round(entry.fat)}F</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick actions — 1 col */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Quick Actions</h2>
          <div className="space-y-2">
            {[
              { icon: Utensils,    label: 'Log a Meal',      sub: 'Add food to today',        to: '/tracker',           gradient: 'from-indigo-500 to-violet-500' },
              { icon: Scale,       label: 'Update Weight',   sub: 'Click the pencil above',   action: () => setEditingWeight(true), gradient: 'from-blue-500 to-cyan-500' },
              { icon: Target,      label: 'Adjust Goals',    sub: 'Recalculate macros',        to: '/macro-calculator',  gradient: 'from-purple-500 to-pink-500' },
              { icon: BarChart2,   label: 'View Progress',   sub: 'Weekly trends',             to: '/progress-reports',  gradient: 'from-orange-500 to-red-500' },
            ].map((item, i) => {
              const Icon = item.icon;
              const inner = (
                <div className="flex items-center gap-3 w-full group">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{item.label}</p>
                    <p className="text-xs text-gray-400">{item.sub}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-indigo-400 transition-colors" />
                </div>
              );
              return item.to ? (
                <Link key={i} to={item.to} className="flex items-center w-full px-3 py-2.5 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
                  {inner}
                </Link>
              ) : (
                <button key={i} onClick={item.action} className="flex items-center w-full px-3 py-2.5 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
                  {inner}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom row: macro remaining pills + USDA CTA */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Protein left',  val: remaining.protein,  unit: 'g', color: 'bg-orange-50 border-orange-100', num: 'text-orange-600' },
          { label: 'Carbs left',    val: remaining.carbs,    unit: 'g', color: 'bg-blue-50 border-blue-100',     num: 'text-blue-600' },
          { label: 'Fat left',      val: remaining.fat,      unit: 'g', color: 'bg-yellow-50 border-yellow-100', num: 'text-yellow-600' },
          { label: 'Calories left', val: remaining.calories, unit: 'kcal', color: 'bg-indigo-50 border-indigo-100', num: 'text-indigo-600' },
        ].map((m, i) => (
          <div key={i} className={`rounded-2xl border p-4 ${m.color}`}>
            <p className="text-xs text-gray-500 font-medium mb-1">{m.label}</p>
            <p className={`text-2xl font-bold ${m.num}`}>{Math.round(m.val)}<span className="text-sm font-normal text-gray-400 ml-1">{m.unit}</span></p>
          </div>
        ))}
      </div>

      {/* Medical disclaimer */}
      <div className="flex gap-2.5 p-3.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800/50">
        <span className="text-amber-500 flex-shrink-0">⚠️</span>
        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
          <strong>Medical Disclaimer:</strong> Nutrition targets shown are general estimates for informational purposes only and do not constitute medical advice. Consult a licensed physician or registered dietitian before making significant dietary changes.
        </p>
      </div>
    </div>
  );
}
